import { Mail, User, LogOut } from "lucide-react";
import { supabase } from "../lib/supabase";

const DASHBOARD_URL = (import.meta.env.VITE_DASHBOARD_URL as string | undefined) ?? "";

interface ProfileScreenProps {
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  email: string | null;
  plan: string | null;
  creditBalance: number | null;
}

function planLabel(plan: string | null): string | null {
  if (!plan) return null;
  if (plan.startsWith("demo")) return "Demo";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function getInitials(
  firstName: string | null,
  lastName: string | null,
  email: string | null,
): string {
  if (firstName || lastName) {
    return [(firstName ?? "")[0], (lastName ?? "")[0]].filter(Boolean).join("").toUpperCase();
  }
  return ((email ?? "?")[0] ?? "?").toUpperCase();
}

export function ProfileScreen({
  firstName,
  lastName,
  role,
  email,
  plan,
  creditBalance,
}: ProfileScreenProps) {
  const displayName =
    [firstName, lastName].filter(Boolean).join(" ").trim() || email || "Your profile";
  const initials = getInitials(firstName, lastName, email);
  const roleLine = [role ? role.charAt(0).toUpperCase() + role.slice(1) : null, planLabel(plan)]
    .filter(Boolean)
    .join(" · ");

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = `${DASHBOARD_URL}/login`;
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto py-4">
      {/* Profile info card */}
      <div className="mx-4">
        {/* Avatar — overlaps card top */}
        <div className="flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-lg font-semibold text-foreground ring-4 ring-background">
            {initials}
          </div>
        </div>

        {/* Card body */}
        <div className="-mt-7 rounded-2xl border border-border bg-card px-5 pb-5 pt-10">
          {/* Name + Credit row */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                Name
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground">{displayName}</p>
            </div>
            {creditBalance !== null && (
              <div className="text-right">
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                  Credit
                </p>
                <p className="mt-0.5 text-sm font-semibold text-primary">
                  ₦{creditBalance.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          <hr className="my-4 border-border" />

          {/* Email row */}
          {email && (
            <div className="flex items-center gap-3 py-1">
              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{email}</span>
            </div>
          )}

          {/* Role + plan row */}
          {roleLine && (
            <div className="flex items-center gap-3 py-1">
              <User className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{roleLine}</span>
            </div>
          )}
        </div>
      </div>

      {/* Sign-out card */}
      <div className="mx-4 mt-3 overflow-hidden rounded-2xl border border-border bg-card">
        <button
          onClick={() => void handleSignOut()}
          className="flex w-full items-center gap-3 px-4 py-3.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  );
}
